import * as React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  ButtonBase,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  dashboardItem,
  navSections,
  isNavItemActive,
} from "../config/navigation";

export const DRAWER_WIDTH = 264;
export const RAIL_WIDTH = 72;

function NavItem({ item, rail, onNavigate }) {
  const theme = useTheme();
  const { shell, brand } = theme.palette;
  const { pathname } = useLocation();
  const active = isNavItemActive(item, pathname);
  const Icon = item.icon;

  const button = (
    <ListItemButton
      component={Link}
      to={item.to}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      aria-label={rail ? item.label : undefined}
      sx={{
        position: "relative",
        height: 40,
        mx: 1,
        my: "2px",
        px: rail ? 0 : 1.5,
        justifyContent: rail ? "center" : "flex-start",
        borderRadius: "10px",
        color: active ? "#fff" : shell.sidebarText,
        bgcolor: active ? alpha(brand.orange, 0.14) : "transparent",
        "&:hover": {
          bgcolor: active ? alpha(brand.orange, 0.2) : shell.sidebarHover,
        },
        "&:focus-visible": {
          outline: `2px solid ${shell.accent}`,
          outlineOffset: 2,
        },
        "&::before": {
          content: '""',
          position: "absolute",
          left: -8,
          top: 8,
          bottom: 8,
          width: 3,
          borderRadius: "0 3px 3px 0",
          bgcolor: shell.accent,
          opacity: active ? 1 : 0,
        },
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: 0,
          mr: rail ? 0 : 1.5,
          color: active ? shell.accent : shell.sidebarMuted,
        }}
      >
        <Icon fontSize="small" />
      </ListItemIcon>
      {!rail && (
        <ListItemText
          primary={item.label}
          slotProps={{
            primary: {
              noWrap: true,
              fontSize: 14,
              fontWeight: active ? 600 : 500,
            },
          }}
        />
      )}
    </ListItemButton>
  );

  return rail ? (
    <Tooltip title={item.label} placement="right">
      {button}
    </Tooltip>
  ) : (
    button
  );
}

function SidebarContent({ rail, onNavigate }) {
  const theme = useTheme();
  const { shell } = theme.palette;
  const navigate = useNavigate();

  const goHome = () => {
    navigate("/dashboard");
    onNavigate?.();
  };

  return (
    <Box
      component="nav"
      aria-label="Main"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: rail ? 0 : 3,
          pt: 3,
          pb: 2,
          textAlign: rail ? "center" : "left",
        }}
      >
        <ButtonBase
          onClick={goHome}
          aria-label="Go to dashboard"
          sx={{
            borderRadius: "8px",
            "&:focus-visible": {
              outline: `2px solid ${shell.accent}`,
              outlineOffset: 2,
            },
          }}
        >
          {rail ? (
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                bgcolor: shell.accent,
                color: "#fff",
                display: "grid",
                placeItems: "center",
                fontWeight: 800,
                fontSize: 20,
              }}
            >
              A
            </Box>
          ) : (
            <Box
              component="img"
              src={theme.custom.alligoLogo}
              alt="Alligo"
              sx={{ width: 140, height: "auto", display: "block" }}
            />
          )}
        </ButtonBase>
        {!rail && (
          <Typography
            sx={{
              mt: 1,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: shell.sidebarMuted,
            }}
          >
            Data Conductor
          </Typography>
        )}
      </Box>

      {/* Sections */}
      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", pb: 2 }}>
        <List disablePadding>
          <NavItem item={dashboardItem} rail={rail} onNavigate={onNavigate} />
        </List>
        {navSections.map((section) => (
          <Box key={section.title} sx={{ mt: 2 }}>
            {rail ? (
              <Box
                sx={{
                  mx: 2,
                  mb: 1,
                  borderTop: `1px solid ${shell.sidebarHover}`,
                }}
              />
            ) : (
              <Typography
                component="h2"
                sx={{
                  px: 3,
                  mb: 0.5,
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: shell.sidebarMuted,
                }}
              >
                {section.title}
              </Typography>
            )}
            <List disablePadding aria-label={section.title}>
              {section.items.map((item) => (
                <NavItem
                  key={item.to}
                  item={item}
                  rail={rail}
                  onNavigate={onNavigate}
                />
              ))}
            </List>
          </Box>
        ))}
      </Box>

      {/* Footer */}
      {!rail && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            p: 2,
            borderTop: `1px solid ${shell.sidebarHover}`,
          }}
        >
          <Typography fontSize={11} color={shell.sidebarMuted}>
            Powered by
          </Typography>
          <Box
            component="img"
            src={theme.custom.avensiaLogo}
            alt="Avensia"
            sx={{ width: 80, height: "auto" }}
          />
        </Box>
      )}
    </Box>
  );
}

function Navigation({ collapsed, mobileOpen, onClose, isDesktop }) {
  const theme = useTheme();
  const { shell } = theme.palette;

  const widthTransition = theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.shorter,
  });

  const paperSx = (width) => ({
    width,
    boxSizing: "border-box",
    bgcolor: shell.sidebarBg,
    color: shell.sidebarText,
    borderRight: "none",
    overflowX: "hidden",
    transition: widthTransition,
  });

  if (isDesktop) {
    const width = collapsed ? RAIL_WIDTH : DRAWER_WIDTH;
    return (
      <Drawer
        variant="permanent"
        open
        sx={{
          width,
          flexShrink: 0,
          whiteSpace: "nowrap",
          transition: widthTransition,
          "& .MuiDrawer-paper": paperSx(width),
        }}
      >
        <SidebarContent rail={collapsed} />
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="temporary"
      open={mobileOpen}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{ "& .MuiDrawer-paper": paperSx(DRAWER_WIDTH) }}
    >
      <SidebarContent rail={false} onNavigate={onClose} />
    </Drawer>
  );
}

export default Navigation;

export const PageCard = ({
  title,
  subTitle,
  children,
  height = "fit-content",
  ...boxProps
}) => {
  return (
    <Box
      bgcolor={"var(--card-bg-color)"}
      borderRadius={3}
      display={"flex"}
      flexDirection={"column"}
      padding={3}
      gap={3}
      height={height}
      {...boxProps}
    >
      {title && (
        <Typography fontWeight={"bold"} variant="h5">
          {title}
        </Typography>
      )}
      {subTitle && <Typography variant={"body1"}>{subTitle}</Typography>}
      {children}
    </Box>
  );
};
